/**
 * Utility functions for Taschenrechner exercise type editor.
 */

import {
  defaultTokenHtmlAdapter,
  DEFAULT_TOKEN_HTML_CONFIG,
  ExtractedToken,
  TaschenrechnerTokenHtmlAdapter,
  TokenHtmlDefinition,
} from './taschenrechner-token-html.adapter';

export {
  defaultTokenHtmlAdapter,
  DEFAULT_TOKEN_HTML_CONFIG,
  ExtractedToken,
  TaschenrechnerTokenHtmlAdapter,
  TokenHtmlDefinition,
};

export interface TokenMatch {
  id: string
  solution: string
  raw: string
}

/**
 * Token pattern: `{id:"<id>" w:"<solution>"}`
 * Supports optional spaces, quotes, and optional commas.
 */
export const TOKEN_REGEX = /\{\s*id\s*:\s*["']([^"']+)["']\s*,?\s*w\s*:\s*["']([^"']*)["']\s*}/gi;

/**
 * Generates a 6-character random hexadecimal ID.
 */
export function generateTokenId(): string {
  const chars = '0123456789abcdef';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

/**
 * Formats a token with id and solution string according to specification.
 */
export function formatToken(id: string, solution: string): string {
  return `{id:"${id}" w:"${solution}"}`;
}

/**
 * Parses all tokens from text in appearance order.
 * Accepts both standard ZAV format `{id:"..." w:"..."}` and editor HTML markup.
 */
export function parseTokens(text: string): TokenMatch[] {
  if (!text) return [];
  const normalized = defaultTokenHtmlAdapter.fromHtmlToZav(text);
  const matches: TokenMatch[] = [];
  const regex = new RegExp(TOKEN_REGEX.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(normalized)) !== null) {
    matches.push({
      id: match[1],
      solution: match[2],
      raw: match[0],
    });
  }
  return matches;
}

/**
 * Updates the solution (w:"...") of a specific token identified by its ID.
 */
export function replaceTokenSolution(text: string, id: string, newSolution: string): string {
  if (!text) return text;
  const regex = new RegExp(`\\{\\s*id\\s*:\\s*["']${id}["']\\s*,?\\s*w\\s*:\\s*["'][^"']*["']\\s*\\}`, 'gi');
  return text.replace(regex, formatToken(id, newSolution));
}

/**
 * Removes a token from the text, reverting it to plain text with its solution content.
 * If the token was embedded inside a formula ($$...$${token}$$...$$), the formula delimiters
 * are properly reconnected.
 */
export function removeTokenFromText(text: string, id: string): string {
  if (!text) return text;

  // Case 1: Token was in the middle of a formula ($$...$${token}$$...$$)
  const middleRegex = new RegExp(`\\$\\$\\s*\\{\\s*id\\s*:\\s*["']${id}["']\\s*,?\\s*w\\s*:\\s*["']([^"']*)["']\\s*}\\s*\\$\\$`, 'gi');
  const replacedMiddle = text.replace(middleRegex, (_match, p1) => p1);
  if (replacedMiddle !== text) {
    return replacedMiddle;
  }

  // Case 2: Token was at the end of a formula ($$...$${token})
  const endRegex = new RegExp(`\\$\\$\\s*\\{\\s*id\\s*:\\s*["']${id}["']\\s*,?\\s*w\\s*:\\s*["']([^"']*)["']\\s*\\}`, 'gi');
  const replacedEnd = text.replace(endRegex, (_match, p1) => `${p1}$$`);
  if (replacedEnd !== text) {
    return replacedEnd;
  }

  // Case 3: Token was at the start of a formula ({token}$$...$$)
  const startRegex = new RegExp(`\\{\\s*id\\s*:\\s*["']${id}["']\\s*,?\\s*w\\s*:\\s*["']([^"']*)["']\\s*}\\s*\\$\\$`, 'gi');
  const replacedStart = text.replace(startRegex, (_match, p1) => `$$${p1}`);
  if (replacedStart !== text) {
    return replacedStart;
  }

  // Case 4: Token was plain text or standalone ({token})
  const defaultRegex = new RegExp(`\\{\\s*id\\s*:\\s*["']${id}["']\\s*,?\\s*w\\s*:\\s*["']([^"']*)["']\\s*\\}`, 'gi');
  return text.replace(defaultRegex, (_match, p1) => p1);
}

/**
 * Checks whether the given string can be converted to equivalent number/fraction representations.
 */
export function isNumberOrFraction(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const first = value.split('||')[0].trim();
  if (!first) return false;
  // Decimal or integer with comma or dot (e.g. "0,42", "-3", "12.5")
  if (/^-?\d+([.,]\d+)?$/.test(first)) return true;
  // Fraction with slash, colon, or calculator fraction symbol ¬ (e.g. "5/12", "5:12", "5¬12")
  return /^-?\d+\s*[/:\u00AC]\s*-?\d+$/.test(first);
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

/**
 * Rational approximation using continued fractions with a tolerance for common didactic fractions.
 */
function continuedFraction(val: number, maxError = 0.005): { n: number, d: number } {
  let m00 = 1;
  let m01 = 0;
  let m10 = 0;
  let m11 = 1;
  let x = val;
  let iterations = 0;

  while (iterations < 20) {
    iterations++;
    const a = Math.floor(x);
    const t0 = m00 * a + m01;
    m01 = m00;
    m00 = t0;
    const t1 = m10 * a + m11;
    m11 = m10;
    m10 = t1;

    if (m10 !== 0 && Math.abs(val - m00 / m10) <= maxError) {
      break;
    }
    if (Math.abs(x - a) < 1e-9) {
      break;
    }
    x = 1 / (x - a);
  }

  const g = gcd(m00, m10);
  return { n: m00 / g, d: m10 / g };
}

/**
 * Automatically generates a list of equivalent mathematical notations:
 * - Decimal fraction with comma (e.g. "0,42")
 * - Fraction with slash (e.g. "5/12")
 * - Division with colon (e.g. "5:12")
 * - Calculator fraction encoding (e.g. "5¬12")
 *
 * Example: "0,42" -> "0,42||5/12||5:12||5¬12"
 */
export function createNumberListFrom(input: string): string {
  if (!input || typeof input !== 'string') return '';
  const first = input.split('||')[0].trim();
  if (!first) return '';

  let n: number;
  let d: number;
  let decimalStr: string;

  const fracMatch = first.match(/^(-?\d+)\s*[/:\u00AC]\s*(-?\d+)$/);
  if (fracMatch) {
    n = parseInt(fracMatch[1], 10);
    d = parseInt(fracMatch[2], 10);
    if (d === 0) return input;
    if (d < 0) {
      n = -n;
      d = -d;
    }
    const g = gcd(n, d);
    n /= g;
    d /= g;

    const val = n / d;
    const rounded4 = Number(val.toFixed(4));
    if (Math.abs(rounded4 - val) < 1e-9) {
      decimalStr = rounded4.toString().replace('.', ',');
    }
    else {
      decimalStr = val.toFixed(2).replace('.', ',');
    }
  }
  else {
    const isNegative = first.startsWith('-');
    const absStr = isNegative ? first.substring(1) : first;
    const decVal = parseFloat(absStr.replace(',', '.'));
    if (isNaN(decVal)) return input;

    if (Number.isInteger(decVal)) {
      return first;
    }

    decimalStr = (isNegative ? '-' : '') + absStr.replace('.', ',');
    const frac = continuedFraction(decVal, 0.005);
    n = (isNegative ? -1 : 1) * frac.n;
    d = frac.d;
  }

  if (d === 1) {
    return n.toString();
  }

  const results = [
    decimalStr,
    `${n}/${d}`,
    `${n}:${d}`,
    `${n}¬${d}`,
  ];

  const unique = Array.from(new Set(results));
  return unique.join('||');
}
